import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import GooglePlacesAddressAutocomplete from 'modules/intake/form/googlePlacesAddressAutocomplete.component';
import { findCountryId, findProvinceId } from 'modules/common/utils';

/**
 * Address Autocomplete Section for Modal Forms
 * This component adds the address search functionality to the create/edit modals
 */
const AddressAutocompleteSection = ({ values, setValues, countriesEnum, provincesEnum }) => {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    // Initialize with existing address if editing
    if (values && values.addressLine1) {
      setSearchValue(values.addressLine1);
    }
  }, [values]);

  const handleAddressSelect = (event, details) => {
    if (details) {
      // Parse the address components and update all fields
      const addressComponents = parseGooglePlaceDetails(details);
      
      setValues({
        ...values,
        addressLine1: addressComponents.addressLine1,
        city: addressComponents.city,
        county: addressComponents.county,
        zipcode: addressComponents.zipcode,
        countryId: findCountryId(addressComponents.country, addressComponents.countryLongName, countriesEnum),
        provinceId: findProvinceId(addressComponents.state, addressComponents.stateLongName, provincesEnum),
      });
      
      setSearchValue(addressComponents.addressLine1);
    }
  };

  const handleInputClear = () => {
    setSearchValue('');
  };

  return (
    <Box sx={{ width: '100%', marginBottom: 2, paddingX: 2 }}>
      <Typography variant="subtitle2" sx={{ marginBottom: 1, color: 'primary.main', fontWeight: 'bold' }}>
        Quick Address Search
      </Typography>
      <GooglePlacesAddressAutocomplete
        label="Address Search"
        placeholder="Type to search and autofill address fields..."
        initialValue={searchValue}
        onSelect={handleAddressSelect}
        onInputClear={handleInputClear}
        onInputEdit={(newValue) => {
          setSearchValue(newValue);
        }}
      />
      <Typography variant="caption" display="block" sx={{ marginTop: 0.5, color: 'text.secondary' }}>
        Search for an address to automatically fill the address fields below, or fill them manually
      </Typography>
    </Box>
  );
};

/**
 * Helper function to parse Google Place details into address components
 */
const parseGooglePlaceDetails = (details) => {
  if (!details || !details.formattedAddress) {
    return {
      addressLine1: '',
      city: '',
      county: '',
      state: '',
      stateLongName: '',
      zipcode: '',
      country: '',
      countryLongName: '',
    };
  }

  // Extract address components from formatted address
  // The details object should have: streetAddress, city, county, state, country from getPlaceDetails
  return {
    addressLine1: details.streetAddress || details.formattedAddress || '',
    city: details.city || '',
    county: details.county || '',
    state: details.state || '',
    stateLongName: details.stateLongName || '',
    zipcode: details.zipcode || '',
    country: details.country || '',
    countryLongName: details.countryLongName || '',
  };
};

export default AddressAutocompleteSection;